export interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    picture: string;
    asesor?: {
      id: string;
      nombre: string;
      correo: string;
      telefono: string;
      cantEmpresas: number;
      rol: string;
    } | null;
  };
}

export interface ValidateResponse {
  valid: boolean;
  user?: {
    id?: string | null;
    email?: string | null;
    sub?: string | null;
  };
  exp: number;
}

export const authService = {
  async login(idToken: string): Promise<AuthResponse> {
    // Note: The endpoint is /auth/sessions, and api.post adds /api prefix if we aren't careful.
    // However, looking at api.ts, it prepends /api to the endpoint.
    // The openapi says /auth/sessions (root level, not /api/auth/sessions usually, but let's check).
    // The openapi.yaml lists paths like /auth/sessions and /api/empresas.
    // api.ts helper adds /api. I might need to bypass api helper or update api helper or usage.
    // The current api.ts says: const response = await fetch(`${API_URL}/api${endpoint}`...
    // If I pass /auth/sessions, it becomes /api/auth/sessions.
    // The openapi yaml shows paths adjacent to each other: /auth/... and /api/...
    // This suggests /auth is NOT under /api.
    // So for Auth I should probably NOT use the api helper if it forces /api prefix, or I should update api helper.
    // Since api helper is hardcoded to /api, I will use fetch directly or create a helper method in authService.

    // Actually, I can just use fetch directly here to avoid modifying api.ts for now, or assume the user wants me to fix api.ts too?
    // User said: "revisa todos los *.service.ts que son los que llaman #file:api.ts"
    // And "AuthContext.tsx consider exporting this to a service handler".

    // I will implementation raw fetch here to respect the path structure.

    const API_URL = import.meta.env.VITE_API_BASE_URL;
    const response = await fetch(`${API_URL}/auth/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Login failed');
    }
    return response.json();
  },

  async localLogin(email: string): Promise<AuthResponse> {
    const API_URL = import.meta.env.VITE_API_BASE_URL;
    const response = await fetch(`${API_URL}/auth/fakelogin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Login failed');
    }
    return response.json();
  },

  async validate(token: string): Promise<ValidateResponse> {
    const API_URL = import.meta.env.VITE_API_BASE_URL;
    const response = await fetch(`${API_URL}/auth/sessions/current`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error('Validation failed');
    }
    return response.json();
  },

  async logout(): Promise<void> {
    const API_URL = import.meta.env.VITE_API_BASE_URL;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    await fetch(`${API_URL}/auth/sessions/current`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};
